import { Button, Loading, notify } from '@affine/component';
import { type AuthAccountInfo, AuthService } from '@affine/core/modules/cloud';
import {
  type Member,
  WorkspaceMembersService,
} from '@affine/core/modules/permissions';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { Permission, WorkspaceMemberStatus } from '@affine/graphql';
import { type I18nString, useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useMemo, useState } from 'react';

import {
  SettingsActionsSection,
  SettingsContentSurface,
  SettingsNotice,
} from '../shared/settings-content';
import {
  SettingsBadge,
  SettingsEntityList,
  SettingsEntityMessage,
} from '../shared/settings-entity';
import { SettingsPersonExpandableItem } from '../shared/settings-identity';
import { WorkspaceRoleBadge } from '../shared/workspace-role-badge';
import { ConfirmAssignModal } from './confirm-assign-modal';
import { useMemberOperationItems } from './member-option';
import * as styles from './styles.css';

export const MemberList = ({
  members,
  isOwner,
  isAdmin,
  goToTeamBilling,
}: {
  members: Member[] | undefined;
  isOwner: boolean;
  isAdmin: boolean;
  goToTeamBilling: () => void;
}) => {
  const session = useService(AuthService).session;
  const account = useLiveData(session.account$);

  if (!account) {
    return null;
  }

  if (members === undefined) {
    return null;
  }

  return (
    <SettingsEntityList>
      {members.map(member => (
        <MemberItem
          currentAccount={account}
          key={member.id}
          member={member}
          isOwner={isOwner}
          isAdmin={isAdmin}
          goToTeamBilling={goToTeamBilling}
        />
      ))}
    </SettingsEntityList>
  );
};

const getShouldShow = ({
  member,
  currentAccountId,
  isOwner,
  isAdmin,
}: {
  member: Member;
  currentAccountId: string;
  isOwner: boolean;
  isAdmin: boolean;
}) => {
  if (
    member.id === currentAccountId ||
    member.permission === Permission.Owner
  ) {
    return false;
  }
  if (isOwner) {
    return true;
  }
  if (isAdmin) {
    return member.permission !== Permission.Admin;
  }
  return false;
};

const MemberItem = ({
  member,
  isOwner,
  isAdmin,
  currentAccount,
  goToTeamBilling,
}: {
  member: Member;
  isAdmin: boolean;
  isOwner: boolean;
  currentAccount: AuthAccountInfo;
  goToTeamBilling: () => void;
}) => {
  const t = useI18n();
  const [expanded, setExpanded] = useState(false);
  const [openAssignModal, setOpenAssignModal] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [pendingRoleChange, setPendingRoleChange] = useState<Permission | null>(
    null
  );
  const membersService = useService(WorkspaceMembersService);
  const workspace = useService(WorkspaceService).workspace;
  const workspaceName = useLiveData(workspace.name$);
  const isEquals = workspaceName === inputValue;

  const showActions = useMemo(
    () =>
      getShouldShow({
        member,
        currentAccountId: currentAccount.id,
        isOwner,
        isAdmin,
      }),
    [member, currentAccount.id, isOwner, isAdmin]
  );

  const handleOpenAssignModal = useCallback(() => {
    setInputValue('');
    setOpenAssignModal(true);
  }, []);

  const confirmAssign = useCallback(() => {
    membersService
      .transferWorkspaceOwnership(member.id)
      .then(result => {
        if (result) {
          setOpenAssignModal(false);
          notify.success({
            title:
              t['com.streamlain.payment.member.team.assign.notify.title'](),
            message: t[
              'com.streamlain.payment.member.team.assign.notify.message'
            ]({
              name: member.name || member.email || member.id,
            }),
          });
          membersService.members.revalidate();
        }
      })
      .catch(error => {
        notify.error({
          title: 'Operation failed',
          message: error.message,
        });
      });
  }, [member, membersService, t]);

  const operationItems = useMemberOperationItems({
    member,
    isOwner,
    isAdmin,
    openAssignModal: handleOpenAssignModal,
    goToTeamBilling,
    pendingRoleChange,
    onRoleChangeStart: setPendingRoleChange,
    onRoleChangeEnd: () => setPendingRoleChange(null),
  }).filter(item => item.show);

  const displayedMember = useMemo(
    () =>
      pendingRoleChange ? { ...member, permission: pendingRoleChange } : member,
    [member, pendingRoleChange]
  );

  const memberStatus = useMemo(() => getMemberStatus(member), [member]);
  const statusLabel = memberStatus ? t.t(memberStatus) : null;

  const primaryBadgeVariant: 'soft' | 'active' | 'error' =
    member.status === WorkspaceMemberStatus.NeedMoreSeat ||
    member.status === WorkspaceMemberStatus.NeedMoreSeatAndReview
      ? 'error'
      : member.status === WorkspaceMemberStatus.Accepted
        ? 'soft'
        : 'active';

  return (
    <>
      <SettingsPersonExpandableItem
        expanded={expanded}
        onToggle={() => setExpanded(prev => !prev)}
        dataTestId="member-item"
        person={member}
        fallbackName={t['Unknown']?.() ?? 'Unknown'}
        badges={
          <>
            <WorkspaceRoleBadge role={displayedMember.permission} />
            {statusLabel ? (
              <SettingsBadge variant={primaryBadgeVariant}>
                {statusLabel}
              </SettingsBadge>
            ) : null}
            {pendingRoleChange ? (
              <SettingsBadge variant="active">
                {t[
                  'com.streamlain.settings.workspace.members.role-updating'
                ]?.() ?? 'Changing role...'}
              </SettingsBadge>
            ) : null}
          </>
        }
      >
        <SettingsContentSurface>
          {showActions ? (
            <SettingsActionsSection
              title={
                t['com.streamlain.settings.workspace.members.actions']?.() ??
                'Actions'
              }
            >
              {operationItems.map(item => (
                <Button
                  key={item.label}
                  variant={item.variant ?? 'secondary'}
                  disabled={item.disabled ?? Boolean(pendingRoleChange)}
                  loading={item.loading}
                  onClick={item.onClick}
                >
                  {item.label}
                </Button>
              ))}
            </SettingsActionsSection>
          ) : (
            <SettingsNotice
              title={
                t[
                  'com.streamlain.settings.workspace.members.no-actions.title'
                ]?.() ?? 'No actions available'
              }
            >
              {t[
                'com.streamlain.settings.workspace.members.no-actions.description'
              ]?.() ??
                'No additional member actions are available for this entry.'}
            </SettingsNotice>
          )}
        </SettingsContentSurface>
      </SettingsPersonExpandableItem>
      <ConfirmAssignModal
        open={openAssignModal}
        setOpen={setOpenAssignModal}
        member={member}
        inputValue={inputValue}
        placeholder={workspaceName}
        setInputValue={setInputValue}
        isEquals={isEquals}
        onConfirm={confirmAssign}
      />
    </>
  );
};

const getMemberStatus = (member: Member): I18nString | null => {
  switch (member.status) {
    case WorkspaceMemberStatus.NeedMoreSeat:
    case WorkspaceMemberStatus.NeedMoreSeatAndReview:
      return 'insufficient-team-seat';
    case WorkspaceMemberStatus.Pending:
      return 'Pending';
    case WorkspaceMemberStatus.UnderReview:
      return 'Under-Review';
    case WorkspaceMemberStatus.AllocatingSeat:
      return 'Allocating Seat';
    case WorkspaceMemberStatus.Accepted:
      return null;
    default:
      return 'Unknown';
  }
};

export const MemberListFallback = () => {
  const t = useI18n();

  return (
    <SettingsEntityMessage>
      <div className={styles.membersFallback}>
        <Loading size={20} />
        <span>{t['com.streamlain.settings.member.loading']()}</span>
      </div>
    </SettingsEntityMessage>
  );
};
